import { type Component, createSignal, createMemo, For } from 'solid-js';
import { gameStore } from '@/stores/gameStore';
import { useTranslation } from '@/i18n';
import LoadingSpinner from './LoadingSpinner';
import {
  DEFAULT_COUNTRY_ISO,
  MIN_NSN_LENGTH,
  countryName,
  flagEmoji,
  getCountry,
  maxNsnLength,
  sortedCountries,
} from '@/data/countries';

interface PhoneVerificationModalProps {
  onSuccess?: () => void;
}

/** "XXX XXX XXX" mask for a fixed-length national number. */
const digitMask = (length: number): string => groupDigits('X'.repeat(length));

/** Group digits in threes: 123456789 -> "123 456 789". */
function groupDigits(value: string): string {
  let formatted = '';
  for (let i = 0; i < value.length; i++) {
    if (i > 0 && i % 3 === 0) {
      formatted += ' ';
    }
    formatted += value[i];
  }
  return formatted;
}

const PhoneVerificationModal: Component<PhoneVerificationModalProps> = (props) => {
  const { t, locale } = useTranslation();
  const [digits, setDigits] = createSignal('');
  const [countryIso, setCountryIso] = createSignal(DEFAULT_COUNTRY_ISO);
  const [error, setError] = createSignal<string | null>(null);
  const [isSubmitting, setIsSubmitting] = createSignal(false);

  const country = createMemo(() => getCountry(countryIso()));
  const countryOptions = createMemo(() => sortedCountries(locale()));
  const maxDigits = createMemo(() => maxNsnLength(country()));

  /** A number is complete when it matches the country's fixed length, or is long enough. */
  const isComplete = createMemo(() => {
    const nsn = country().nsn;
    const length = digits().length;
    return nsn ? length === nsn : length >= MIN_NSN_LENGTH;
  });

  const hint = createMemo(() => {
    const { dial, nsn } = country();
    return nsn
      ? t('phoneVerification.phoneHint', { dial, mask: digitMask(nsn) })
      : t('phoneVerification.phoneHintGeneric', { dial });
  });

  const handleCountryChange = (e: Event) => {
    const iso = (e.currentTarget as HTMLSelectElement).value;
    setCountryIso(iso);
    // Re-clamp the number to the new country's limit (e.g. +48 allows 9 digits, +995 up to 12).
    setDigits((current) => current.slice(0, maxNsnLength(getCountry(iso))));
    setError(null);
  };

  const handlePhoneChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    setDigits(target.value.replace(/\D/g, '').slice(0, maxDigits()));
    setError(null);
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();

    if (!isComplete()) {
      setError(t('phoneVerification.errorInvalidFormat'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const fullNumber = `${country().dial}${digits()}`;
      const result = await gameStore.verifyPhone(fullNumber);

      if (result.success) {
        props.onSuccess?.();
      } else {
        setError(result.error || t('phoneVerification.errorInvalidFormat'));
      }
    } catch (err: any) {
      setError(err.message || t('errors.unknownError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div class="w-full max-w-md">
        <div class="card animate-fade-in">
          <div class="card-header text-center">
            <div class="text-5xl mb-4">
              <span role="img" aria-label="phone">📱</span>
            </div>
            <h2 class="text-2xl font-bold text-white mb-2">
              {t('phoneVerification.title')}
            </h2>
            <p class="text-primary-200">
              {t('phoneVerification.subtitle')}
            </p>
          </div>

          <div class="card-body">
            <form onSubmit={handleSubmit} noValidate>
              <div class="form-group">
                <label for="country" class="form-label">
                  {t('phoneVerification.country')}
                </label>
                <select
                  id="country"
                  class="form-input"
                  value={countryIso()}
                  onChange={handleCountryChange}
                  disabled={isSubmitting()}
                >
                  <For each={countryOptions()}>
                    {(option) => (
                      <option value={option.iso}>
                        {flagEmoji(option.iso)} {countryName(option.iso, locale())} ({option.dial})
                      </option>
                    )}
                  </For>
                </select>
              </div>

              <div class="form-group">
                <label for="phone" class="form-label">
                  {t('phoneVerification.phoneNumber')}
                </label>

                <div class="flex gap-2">
                  {/* Dialling code of the selected country */}
                  <div class="w-24 flex-shrink-0">
                    <div class="form-input text-center font-medium cursor-default">
                      {country().dial}
                    </div>
                  </div>

                  {/* Phone Number Input */}
                  <div class="flex-1">
                    <input
                      type="tel"
                      id="phone"
                      class={`form-input ${error() ? 'error' : ''}`}
                      placeholder={t('phoneVerification.phonePlaceholder')}
                      value={groupDigits(digits())}
                      onInput={handlePhoneChange}
                      disabled={isSubmitting()}
                      autocomplete="tel-national"
                      inputMode="numeric"
                      pattern="[0-9\s]*"
                    />
                  </div>
                </div>

                <div class="text-sm text-primary-300 mt-2">
                  {hint()}
                </div>

                {error() && (
                  <div class="form-error mt-2">
                    {error()}
                  </div>
                )}
              </div>

              <button
                type="submit"
                class="btn btn-primary btn-full btn-lg mt-6"
                disabled={isSubmitting() || !isComplete()}
              >
                {isSubmitting() ? (
                  <>
                    <LoadingSpinner size="sm" />
                    {t('phoneVerification.submitting')}
                  </>
                ) : (
                  <>
                    <span role="img" aria-hidden="true">✓</span>
                    {t('phoneVerification.submit')}
                  </>
                )}
              </button>
            </form>
          </div>

          <div class="card-footer">
            <div class="text-center text-primary-300 text-sm">
              <p>
                <span role="img" aria-label="info">ℹ️</span>
                {' '}{t('phoneVerification.privacyNote')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneVerificationModal;
